import classNames from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import _startCase from 'lodash/startCase';
import type { ReactElement } from 'react';
import { useRef, useState } from 'react';
import { Grid, Icon, Popup } from 'semantic-ui-react';

import type { Field } from '@models';
import ConditionalWrap from '@components/conditional-wrap';
import FieldEditor from '@components/field-list-editor/field-editor';
import TruncateText from '@components/truncate-text';
import { isFieldEmpty } from '@utils/common';

import FieldFormatter from './field-formatter';
// eslint-disable-next-line postcss-modules/no-unused-class
import s from './pills.scss';

export type PillElement = Field & {
  // Can the user click on the value or label?
  clickable?: boolean;

  // Should the pill be removable?
  // If so, a close icon will be displayed at the top right of the pill
  removable?: boolean;

  // Can the selected value in the field editor be cleared?
  clearable?: boolean;

  // CSS class to use for displaying the field editor input
  editorInputClass?: string;
};

export interface PillsProps {
  elements: Array<PillElement>;
  editInline?: boolean;
  onUpdatePill?: (pill: Field) => void;
  onClickPill?: (pill: Field, element: HTMLElement | null) => void;
  onRemovePill?: (pill: Field, element: HTMLElement | null) => void;
  showValues?: boolean;
  displayBefore?: ReactElement;
  displayAfter?: ReactElement;
  pillClassName?: string;
}

interface PillProps {
  pill: PillElement;
  editInline?: boolean;
  showValues?: boolean;
  onUpdatePill?: (pill: Field) => void;
  onClickPill?: (pill: Field, element: HTMLElement | null) => void;
  onRemovePill?: (pill: Field, element: HTMLElement | null) => void;
}
const Pill = ({
  pill,
  onClickPill,
  onRemovePill,
  onUpdatePill,
  editInline,
  showValues,
}: PillProps) => {
  const clickButtonRef = useRef<HTMLButtonElement>(null);
  const removeButtonRef = useRef<HTMLButtonElement>(null);
  const shouldValidateEditing =
    editInline &&
    ['String', 'Int', 'Enum', 'EnumList', 'Float', 'BigInt'].includes(
      pill.typeName ?? 'String',
    );
  const [editing, setEditing] = useState(
    shouldValidateEditing && isFieldEmpty(pill),
  );
  const valueInput = useRef<HTMLInputElement>(null);
  const name =
    pill.category && pill.name
      ? `${pill.category} ${pill.name}`
      : pill.name || pill.key;
  const labelElement = shouldValidateEditing ? (
    <button
      type="button"
      className={s.key}
      onClick={() => {
        if (showValues) {
          setEditing(true);
          valueInput.current?.focus();
        }
      }}
    >
      {name}
    </button>
  ) : (
    <div className={s.key}>{name}</div>
  );
  const inputId = `field_editor_input_${pill.key}`;

  return (
    <>
      {pill.description ? (
        <Popup
          position="top center"
          trigger={labelElement}
          content={pill.description}
          mouseEnterDelay={500}
        />
      ) : (
        labelElement
      )}
      {showValues ? (
        <>
          <ConditionalWrap
            condition={!!onClickPill}
            wrap={children => (
              <button
                ref={clickButtonRef}
                type="button"
                className={s.pillButton}
                onClick={() => onClickPill!(pill, clickButtonRef.current)}
              >
                {children}
              </button>
            )}
          >
            {editInline && onUpdatePill ? (
              <div
                className={classNames(s.value, editing ? s.inlineEditing : '')}
              >
                {editing || !shouldValidateEditing ? (
                  <FieldEditor
                    inputRef={valueInput}
                    inputId={inputId}
                    label={
                      <label
                        htmlFor={inputId}
                        style={{ backgroundColor: 'white' }}
                        className="darkreader-ignore-inline-style darkreader-override"
                      >
                        <span>
                          {(pill.data ?? pill.example) ? 'True' : 'False'}
                        </span>
                      </label>
                    }
                    defaultOpen
                    selected
                    validateChanges
                    className={s.fieldEditor}
                    inputClassName={classNames(
                      s[`editor-${pill.typeName ?? 'String'}`],
                      pill.editorInputClass,
                    )}
                    field={pill}
                    onChange={newField => {
                      onUpdatePill({ ...pill, data: newField.data } as Field);
                      setEditing(false);
                    }}
                    showBooleansAsToggles
                    clearable={pill.clearable}
                  />
                ) : (
                  <button
                    type="button"
                    className={classNames(s.fieldEditor, s.readOnly)}
                    onClick={() => setEditing(true)}
                  >
                    <TruncateText>
                      <FieldFormatter field={pill} />
                    </TruncateText>
                  </button>
                )}
              </div>
            ) : (
              <FieldFormatter field={pill} className={s.value} />
            )}
          </ConditionalWrap>
          {onRemovePill && !pill.required ? (
            <button
              aria-label="Remove"
              className={s.pillRemoveButton}
              ref={removeButtonRef}
              type="button"
              onClick={() => onRemovePill!(pill, removeButtonRef.current)}
            >
              <Icon name="times circle" />
            </button>
          ) : null}
        </>
      ) : null}
    </>
  );
};

const Pills = ({
  elements,
  onClickPill = undefined,
  onRemovePill = undefined,
  showValues = true,
  editInline,
  onUpdatePill,
  displayBefore,
  displayAfter,
  pillClassName,
}: PillsProps) => (
  <div className={s.root}>
    <Grid columns="1">
      <Grid.Row>
        <Grid.Column>
          <ul
            className={classNames(s.pillList, editInline ? s.editInline : '')}
          >
            {displayBefore ? (
              <li className={classNames(s.pill, pillClassName)}>
                {displayBefore}
              </li>
            ) : null}
            {elements.map(pill => (
              <li
                className={classNames(s.pill, pillClassName)}
                key={pill.key}
                aria-label={pill.name || _startCase(pill.key)}
              >
                <Pill
                  pill={pill}
                  showValues={showValues}
                  editInline={editInline}
                  onClickPill={
                    (pill.clickable ?? true) ? onClickPill : undefined
                  }
                  onRemovePill={
                    (pill.removable ?? true) ? onRemovePill : undefined
                  }
                  onUpdatePill={editInline ? onUpdatePill : undefined}
                />
              </li>
            ))}
            {displayAfter ? (
              <li className={classNames(s.pill, pillClassName)}>
                {displayAfter}
              </li>
            ) : null}
          </ul>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  </div>
);

export default withStyles(s)(Pills);
